import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/* ─── Google Fonts ─── */
const interFontLink = document.createElement('link');
interFontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
interFontLink.rel = 'stylesheet';
document.head.appendChild(interFontLink);

const playfairLink = document.createElement('link');
playfairLink.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;1,700&display=swap';
playfairLink.rel = 'stylesheet';
document.head.appendChild(playfairLink);

/* ─── Scroll-reveal wrapper ─── */
const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

/* ─── Award data ─── */
const AWARDS = [
  {
    image: '/Images/Prize1.jpeg',
    alt: 'IPE Awards 1st Prize',
    category: 'IPE AWARDS',
    title: 'Industrial Print Excellence (IPE) Awards',
    description:
      "Decaltech was honored with the 1st Prize at the Industrial Print Excellence (IPE) Awards, recognizing the company's commitment to innovation, superior print quality, precision manufacturing, and excellence in industrial printing solutions. This achievement reflects our dedication to delivering world-class products and maintaining the highest industry standards.",
    reverse: false,
  },
  {
    image: '/Images/award2.png',
    alt: 'FESPA Awards 2025 Bronze Winner — Decal Tech',
    category: 'FESPA AWARDS · 2025',
    title: 'World-Level Recognition – FESPA Awards 2025',
    description:
      'Decaltech proudly received the Bronze Award at the FESPA Awards 2025, earning global recognition for excellence in Decals and Printed Labels. This prestigious international achievement reflects our commitment to innovation, outstanding print quality, and delivering world-class industrial printing solutions.',
    reverse: true,
    containImage: true,
  },
];

/* ─── Award Section Row ─── */
const AwardRow = ({ award, delay, isLast }) => {
  const showFullImage = award.containImage;

  return (
    <>
      <Reveal delay={delay}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '80px',
          alignItems: 'center',
          padding: '80px 0',
          direction: award.reverse ? 'rtl' : 'ltr',
        }}>
          {/* Image side */}
          <div style={{ direction: 'ltr', position: 'relative' }}>
            <motion.div
              whileHover={{ scale: showFullImage ? 1.01 : 1.02 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              style={{
                borderRadius: '4px',
                overflow: 'hidden',
                lineHeight: 0,
                background: showFullImage ? '#f0f0f0' : 'transparent',
                border: showFullImage ? '1px solid #e8e8e8' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: showFullImage ? '360px' : undefined,
                padding: showFullImage ? '16px' : 0,
              }}
            >
              <img
                src={award.image}
                alt={award.alt}
                style={{
                  width: '100%',
                  height: showFullImage ? 'auto' : '380px',
                  maxHeight: showFullImage ? '420px' : undefined,
                  objectFit: showFullImage ? 'contain' : 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  borderRadius: '4px',
                }}
              />
            </motion.div>
          </div>

          {/* Text side */}
          <div style={{ direction: 'ltr', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Category label */}
            <p style={{
              margin: 0,
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: '#cc0000',
              fontFamily: "'Inter', sans-serif",
            }}>
              {award.category}
            </p>

            {/* Title */}
            <h2 style={{
              margin: 0,
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(1.6rem, 2.5vw, 2.1rem)',
              fontWeight: 700,
              color: '#1a1a1a',
              lineHeight: 1.25,
            }}>
              {award.title}
            </h2>

            {/* Red underline */}
            <div style={{
              width: '40px',
              height: '3px',
              background: '#cc0000',
              borderRadius: '2px',
            }} />

            {/* Description */}
            <p style={{
              margin: 0,
              fontFamily: "'Inter', sans-serif",
              fontSize: '15px',
              color: '#555',
              lineHeight: '1.85',
            }}>
              {award.description}
            </p>
          </div>
        </div>
      </Reveal>

      {/* Divider between rows (not after last) */}
      {!isLast && (
        <div style={{ height: '1px', background: '#e8e8e8' }} />
      )}
    </>
  );
};

/* ══════════════════════════════════════════════
    GALLERY SCREEN
══════════════════════════════════════════════ */
const Galleryscreen = () => {
  return (
    <div style={{
      backgroundColor: '#fafafa',
      minHeight: '100vh',
      overflowX: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>

      {/* ── Page Header ── */}
      <div style={{
        textAlign: 'center',
        padding: '80px 40px 64px',
        background: '#fff',
        borderBottom: '1px solid #efefef',
      }}>
        <Reveal delay={0}>
          <p style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: '#cc0000',
            margin: '0 0 16px',
            fontFamily: "'Inter', sans-serif",
          }}>
            Awards &amp; Recognition
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            fontWeight: 800,
            color: '#1a1a1a',
            margin: '0 0 18px',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
          }}>
            Our Achievements
          </h1>
        </Reveal>
        <Reveal delay={0.18}>
          <p style={{
            fontSize: '16px',
            color: '#666',
            maxWidth: '520px',
            margin: '0 auto',
            lineHeight: '1.7',
            fontFamily: "'Inter', sans-serif",
          }}>
            Recognised on national and global stages for innovation, quality, and excellence in industrial printing.
          </p>
        </Reveal>
      </div>

      {/* ── Awards List ── */}
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '0 60px 100px',
        background: '#fafafa',
      }}>
        {AWARDS.map((award, i) => (
          <AwardRow
            key={award.title}
            award={award}
            delay={0.1}
            isLast={i === AWARDS.length - 1}
          />
        ))}
      </div>

    </div>
  );
};

export default Galleryscreen;